import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Bell, LayoutGrid, MessagesSquare, Gauge, Check, Copy, Apple, Monitor,
  RefreshCw, LifeBuoy, ShieldCheck, Keyboard,
} from 'lucide-react';
import Shell from '../components/TopBarShell';
import { useLocale } from '../contexts/LocaleProvider';
import { LT } from '../contexts/AppCatalogueContext';

/* Cody · LampTwist's app for running Claude Code sessions. A launch page:
 * what it is, which platform, and how to install it step by step. The copy
 * lives here (not in translations.js) because it's one long page in FR/NL/EN. */

const REPO = 'https://github.com/LampTwist/cody';
const RELEASES = `${REPO}/releases/latest`;
const CONTACT = 'geoffrey@lamptwist.be';

const detectPlatform = () => {
  const p = (navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || '').toLowerCase();
  return p.includes('win') ? 'windows' : 'mac';
};

export default function CodyGuide() {
  const { locale } = useLocale();
  const c = COPY[locale] || COPY.fr;
  const [platform, setPlatform] = useState(detectPlatform);
  const steps = platform === 'windows' ? c.windows.steps : c.mac.steps;

  return (
    <Shell>
      <Link to="/apps" className="inline-flex items-center gap-1.5 text-[13px] mb-4" style={{ color: LT.green50 }}>
        <ArrowLeft className="h-3.5 w-3.5" /> {c.back}
      </Link>

      {/* Hero */}
      <header className="flex items-center gap-5 max-w-[1100px]">
        <img src="/cody-assets/icon.png" alt="" className="h-20 w-20 rounded-[22px] shrink-0"
             style={{ boxShadow: '0 8px 24px rgba(43,65,56,0.18)' }} />
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.16em] font-medium" style={{ color: LT.green50 }}>{c.eyebrow}</div>
          <h1 className="font-display text-[2.4rem] leading-tight tracking-[-0.02em]" style={{ color: LT.greenDeep }}>Cody</h1>
          <p className="text-[16px] mt-1 max-w-[640px]" style={{ color: '#3a4f44' }}>{c.tagline}</p>
        </div>
      </header>

      {/* One-minute intro video (with sound) */}
      <section className="mt-8 max-w-[1100px]">
        <video src="/cody-assets/cody-launch.mp4" poster="/cody-assets/cody-launch-poster.jpg" controls preload="metadata" playsInline
               className="w-full rounded-2xl block" style={{ background: '#0B0A0D', boxShadow: '0 16px 40px rgba(43,65,56,0.18)' }} />
        <div className="text-[12.5px] mt-2" style={{ color: LT.green50 }}>{c.videoCaption}</div>
      </section>

      {/* What it does */}
      <section className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6 max-w-[1100px]">
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {c.features.map((f, i) => {
            const Icon = [Bell, LayoutGrid, MessagesSquare, Gauge][i];
            return (
              <div key={f.title} className="rounded-2xl p-4" style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
                <div className="h-8 w-8 rounded-lg flex items-center justify-center mb-2" style={{ background: 'rgba(217,119,87,0.12)' }}>
                  <Icon className="h-4 w-4" style={{ color: '#C4623F' }} />
                </div>
                <div className="text-[14.5px] font-medium" style={{ color: LT.greenDeep }}>{f.title}</div>
                <div className="text-[13px] mt-1 leading-relaxed" style={{ color: '#4d6157' }}>{f.text}</div>
              </div>
            );
          })}
        </div>
        <figure className="lg:col-span-5 m-0">
          <img src="/cody-assets/session.png" alt={c.screenshotAlt} className="w-full rounded-2xl"
               style={{ border: `1px solid ${LT.green30}`, boxShadow: '0 10px 30px rgba(43,65,56,0.14)' }} />
          <figcaption className="text-[12px] mt-2" style={{ color: LT.green50 }}>{c.screenshotCaption}</figcaption>
        </figure>
      </section>

      {/* Who it's for */}
      <section className="mt-8 max-w-[1100px] rounded-2xl p-5 flex items-start gap-3"
               style={{ background: LT.yellowLight, border: `1px solid ${LT.yellow}` }}>
        <ShieldCheck className="h-5 w-5 shrink-0 mt-0.5" style={{ color: LT.greenDeep }} />
        <div className="text-[14px] leading-relaxed" style={{ color: LT.greenDeep }}>
          <b>{c.needTitle}</b> {c.needText}
        </div>
      </section>

      {/* Platform choice */}
      <section className="mt-10 max-w-[1100px]">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display text-[1.6rem]" style={{ color: LT.greenDeep }}>{c.installTitle}</h2>
            <p className="text-[14px] mt-1" style={{ color: LT.green50 }}>{c.installSub}</p>
          </div>
          <div className="inline-flex p-1 rounded-xl" style={{ background: LT.beigeWarm }}>
            {[['mac', Apple, 'Mac'], ['windows', Monitor, 'Windows']].map(([key, Icon, label]) => (
              <button key={key} type="button" onClick={() => setPlatform(key)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[14px] transition"
                      style={platform === key
                        ? { background: '#fff', color: LT.greenDeep, boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }
                        : { color: LT.green50 }}>
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-2 text-[13px]" style={{ color: LT.green50 }}>
          {platform === 'windows' ? c.windows.intro : c.mac.intro}
        </div>

        <ol className="mt-5 space-y-3 list-none p-0">
          {steps.map((s, i) => <Step key={`${platform}-${i}`} n={i + 1} step={s} copyLabel={c.copy} copiedLabel={c.copied} />)}
        </ol>
      </section>

      {/* Updates, shortcuts, help */}
      <section className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-4 max-w-[1100px] mb-16">
        <Card icon={RefreshCw} title={c.updatesTitle}>{c.updatesText}</Card>
        <Card icon={Keyboard} title={c.shortcutsTitle}>
          <table className="w-full text-[13px]">
            <tbody>
              {(platform === 'windows' ? c.shortcutsWin : c.shortcutsMac).map(([k, v]) => (
                <tr key={k}>
                  <td className="py-1 pr-3 whitespace-nowrap">
                    <kbd className="px-1.5 py-0.5 rounded text-[11.5px]"
                         style={{ background: LT.beigeWarm, border: `1px solid ${LT.green30}`, fontFamily: 'ui-monospace, monospace' }}>{k}</kbd>
                  </td>
                  <td className="py-1" style={{ color: '#4d6157' }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card icon={LifeBuoy} title={c.helpTitle}>
          <ul className="space-y-2 m-0 p-0 list-none">
            {c.help.map(([q, a]) => (
              <li key={q}><b style={{ color: LT.greenDeep }}>{q}</b><br /><span style={{ color: '#4d6157' }}>{a}</span></li>
            ))}
          </ul>
          <div className="mt-3 pt-3 border-t text-[13px]" style={{ borderColor: LT.green30, color: '#4d6157' }}>
            {c.contact} <a href={`mailto:${CONTACT}`} className="underline" style={{ color: LT.greenDeep }}>{CONTACT}</a>
          </div>
        </Card>
      </section>
    </Shell>
  );
}

function Step({ n, step, copyLabel, copiedLabel }) {
  return (
    <li className="rounded-2xl p-5 flex gap-4" style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
      <div className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 text-[14px] font-medium"
           style={{ background: LT.greenDeep, color: LT.beigeLight }}>{n}</div>
      <div className="min-w-0 flex-1">
        <div className="text-[15.5px] font-medium" style={{ color: LT.greenDeep }}>{step.title}</div>
        {step.text && <p className="text-[14px] mt-1 leading-relaxed m-0" style={{ color: '#4d6157' }}>{renderLinks(step.text)}</p>}
        {step.code && <CodeLine code={step.code} copyLabel={copyLabel} copiedLabel={copiedLabel} />}
        {step.after && <p className="text-[13px] mt-2 leading-relaxed m-0" style={{ color: LT.green50 }}>{renderLinks(step.after)}</p>}
        {step.button && (
          <a href={step.button.href} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1.5 mt-3 px-3.5 py-2 rounded-md text-[13.5px]"
             style={{ background: LT.greenDeep, color: LT.beigeLight }}>
            {step.button.label}
          </a>
        )}
      </div>
    </li>
  );
}

function CodeLine({ code, copyLabel, copiedLabel }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-3 flex items-stretch rounded-lg overflow-hidden" style={{ border: `1px solid ${LT.green30}` }}>
      <code className="flex-1 px-3 py-2.5 text-[13px] overflow-x-auto whitespace-nowrap"
            style={{ background: '#1f2a25', color: '#E9EFEA', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>
        {code}
      </code>
      <button type="button"
              onClick={() => { navigator.clipboard?.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
              className="px-3 inline-flex items-center gap-1.5 text-[12.5px] transition hover:bg-black/[0.04]"
              style={{ background: LT.beigeLight, color: LT.greenDeep }}>
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? copiedLabel : copyLabel}
      </button>
    </div>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="rounded-2xl p-5" style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-medium" style={{ color: LT.green50 }}>
        <Icon className="h-3.5 w-3.5" /> {title}
      </div>
      <div className="mt-2 text-[13.5px] leading-relaxed" style={{ color: '#4d6157' }}>{children}</div>
    </div>
  );
}

/* "[label](url)" links and `code` inside the copy. */
function renderLinks(text) {
  const parts = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)|`([^`]+)`/g;
  let last = 0, m;
  while ((m = re.exec(text))) {
    parts.push(text.slice(last, m.index));
    parts.push(m[3] !== undefined
      ? <code key={m.index} className="px-1 py-0.5 rounded text-[12.5px]"
              style={{ background: LT.beigeWarm, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>{m[3]}</code>
      : <a key={m.index} href={m[2]} target="_blank" rel="noreferrer" className="underline" style={{ color: LT.greenDeep }}>{m[1]}</a>);
    last = m.index + m[0].length;
  }
  parts.push(text.slice(last));
  return parts;
}

// MARK: Copy (FR canonical, NL, EN)

const MAC_INSTALL = 'git clone https://github.com/LampTwist/cody.git ~/cody && ~/cody/build.sh install';

const COPY = {
  fr: {
    videoCaption: 'Cody en une minute · avec le son.',
    back: 'Retour aux apps',
    eyebrow: 'Outil interne · Mac & Windows',
    tagline: "Toutes tes sessions Claude Code dans une seule app calme. Cody te dit quand Claude a fini ou a besoin de toi, et rend les conversations faciles à lire.",
    features: [
      { title: 'Il te prévient', text: "Une notification quand Claude a terminé ou attend une réponse, même quand tu es dans une autre app. Slack ou téléphone quand tu es absent." },
      { title: 'Tout au même endroit', text: "Dix projets ouverts ? Chaque session a son statut en direct dans la barre latérale : au travail, t'attend, terminé." },
      { title: 'Terminal ou chat', text: "Le vrai Claude Code, ou une vue chat lisible avec une zone de message, des captures d'écran et des prompts prêts à l'emploi." },
      { title: 'Tes limites en vue', text: "Contexte, coût et tes limites (5 h, semaine, Fable) en un coup d'œil, avec la part de chaque projet." },
    ],
    screenshotAlt: 'Une session Cody',
    screenshotCaption: 'Claude Code à gauche, la conversation lisible à droite.',
    needTitle: 'Ce qu’il te faut :',
    needText: "un compte Claude (équipe LampTwist) avec Claude Code installé, et un accès GitHub au dépôt LampTwist/cody (demande-le à Geoffrey). Compte environ 10 minutes, une seule fois.",
    installTitle: 'Installer Cody',
    installSub: 'Choisis ton ordinateur. On l’a deviné pour toi.',
    copy: 'Copier', copied: 'Copié',
    mac: {
      intro: 'Sur Mac, Cody se construit sur ta machine (environ une minute) : pas besoin de compte développeur Apple.',
      steps: [
        { title: 'Installe Claude Code et connecte-toi', text: "Ouvre Terminal (⌘ Espace, tape « Terminal ») et vérifie :", code: 'claude --version', after: "Tu vois un numéro de version ? Parfait. Sinon installe Claude Code depuis [claude.com/code](https://claude.com/code), puis lance `claude` une fois pour te connecter." },
        { title: 'Installe les outils développeur d’Apple', code: 'xcode-select --install', after: "Une fenêtre s'ouvre : clique sur Installer. S'il dit qu'ils sont déjà installés, c'est bon." },
        { title: 'Connecte GitHub', text: "Accepte l'invitation à LampTwist/cody sur GitHub, puis :", code: 'gh auth login', after: "Choisis GitHub.com, HTTPS, et « Login with a web browser ». Pas de `gh` ? `brew install gh`." },
        { title: 'Installe Cody', code: MAC_INSTALL, after: "Cody se construit et s'ouvre. Il est ensuite dans Applications. Garde le dossier `cody` : c'est de là que viennent les mises à jour." },
        { title: 'Premier lancement', text: "Autorise les notifications, puis appuie sur ⌘T pour choisir un projet. C’est parti." },
      ],
    },
    windows: {
      intro: "Sur Windows, tu télécharges un installateur. Windows 10 ou 11.",
      steps: [
        { title: 'Installe Claude Code et connecte-toi', text: 'Ouvre PowerShell et lance :', code: 'irm https://claude.ai/install.ps1 | iex', after: "Puis tape `claude` une fois pour te connecter avec ton compte LampTwist." },
        { title: 'Installe Git pour Windows', text: "Claude Code en a besoin pour ses outils. Garde les options par défaut.", button: { label: 'Télécharger Git', href: 'https://git-scm.com/downloads/win' } },
        { title: 'Télécharge Cody', text: "Connecte-toi à GitHub (accès LampTwist/cody) et prends Cody-Setup-….exe.", button: { label: 'Dernière version de Cody', href: RELEASES } },
        { title: 'Lance l’installateur', text: "Windows peut afficher « Windows a protégé votre ordinateur » : c'est normal pour une app interne. Clique sur « Informations complémentaires », puis « Exécuter quand même ». Cody s'installe et s'ouvre." },
        { title: 'Premier lancement', text: "Autorise les notifications, puis Ctrl+Shift+T pour choisir un projet. Fermer la fenêtre garde Cody dans la barre des tâches (icône près de l'horloge)." },
      ],
    },
    updatesTitle: 'Mises à jour',
    updatesText: "Automatiques. Quand une nouvelle version sort, tu reçois une notification et un bandeau « Mise à jour disponible » : clique sur Update, puis Restart. Tes sessions reviennent après le redémarrage.",
    shortcutsTitle: 'Raccourcis utiles',
    shortcutsMac: [['⌘K', 'Toutes les commandes'], ['⌘T', 'Nouvelle session'], ['⌘E', 'Terminal ou chat'], ['⌘J', 'Session qui t’attend'], ['⌘1…9', 'Changer de session']],
    shortcutsWin: [['Ctrl+Shift+K', 'Toutes les commandes'], ['Ctrl+Shift+T', 'Nouvelle session'], ['Ctrl+Shift+E', 'Terminal ou chat'], ['Ctrl+Shift+J', 'Session qui t’attend'], ['Ctrl+1…9', 'Changer de session']],
    helpTitle: 'Un souci ?',
    help: [
      ['« claude » introuvable', "Claude Code n'est pas installé ou pas connecté : refais l'étape 1."],
      ['GitHub dit « not found »', "Tu n'as pas encore accès à LampTwist/cody : demande l'invitation."],
      ['Sur Windows, Claude parle de Git Bash', "Installe Git pour Windows (étape 2), puis redémarre Cody."],
    ],
    contact: 'Toujours bloqué ? Envoie une capture d’écran à',
  },
  nl: {
    videoCaption: 'Cody in één minuut · met geluid.',
    back: 'Terug naar apps',
    eyebrow: 'Interne tool · Mac & Windows',
    tagline: 'Al je Claude Code-sessies in één rustige app. Cody laat weten wanneer Claude klaar is of je nodig heeft, en maakt gesprekken makkelijk leesbaar.',
    features: [
      { title: 'Het verwittigt je', text: 'Een melding wanneer Claude klaar is of op je antwoord wacht, ook als je in een andere app zit. Slack of je telefoon als je weg bent.' },
      { title: 'Alles op één plek', text: 'Tien projecten open? Elke sessie toont live haar status in de zijbalk: bezig, wacht op jou, klaar.' },
      { title: 'Terminal of chat', text: 'De echte Claude Code, of een leesbare chatweergave met een berichtvak, screenshots en kant-en-klare prompts.' },
      { title: 'Je limieten in beeld', text: 'Context, kosten en je limieten (5 u, week, Fable) in één oogopslag, met het aandeel van elk project.' },
    ],
    screenshotAlt: 'Een Cody-sessie',
    screenshotCaption: 'Claude Code links, het leesbare gesprek rechts.',
    needTitle: 'Wat je nodig hebt:',
    needText: 'een Claude-account (LampTwist-team) met Claude Code geïnstalleerd, en GitHub-toegang tot LampTwist/cody (vraag het aan Geoffrey). Reken op ongeveer 10 minuten, één keer.',
    installTitle: 'Cody installeren',
    installSub: 'Kies je computer. We hebben het al geraden.',
    copy: 'Kopiëren', copied: 'Gekopieerd',
    mac: {
      intro: 'Op de Mac wordt Cody op je eigen toestel gebouwd (ongeveer een minuut): geen Apple-ontwikkelaarsaccount nodig.',
      steps: [
        { title: 'Installeer Claude Code en meld je aan', text: 'Open Terminal (⌘ Spatie, typ "Terminal") en controleer:', code: 'claude --version', after: 'Zie je een versienummer? Top. Anders installeer je Claude Code via [claude.com/code](https://claude.com/code) en start je `claude` één keer om je aan te melden.' },
        { title: 'Installeer Apple’s ontwikkelaarstools', code: 'xcode-select --install', after: 'Er verschijnt een venster: klik op Installeer. Zegt het dat ze al geïnstalleerd zijn, dan is het goed.' },
        { title: 'Verbind GitHub', text: 'Aanvaard de uitnodiging voor LampTwist/cody op GitHub, en dan:', code: 'gh auth login', after: 'Kies GitHub.com, HTTPS en "Login with a web browser". Geen `gh`? `brew install gh`.' },
        { title: 'Installeer Cody', code: MAC_INSTALL, after: 'Cody wordt gebouwd en opent. Daarna staat het in Programma’s. Hou de map `cody`: daar komen de updates vandaan.' },
        { title: 'Eerste keer', text: 'Sta meldingen toe en druk op ⌘T om een project te kiezen. Klaar.' },
      ],
    },
    windows: {
      intro: 'Op Windows download je een installatieprogramma. Windows 10 of 11.',
      steps: [
        { title: 'Installeer Claude Code en meld je aan', text: 'Open PowerShell en voer uit:', code: 'irm https://claude.ai/install.ps1 | iex', after: 'Typ daarna één keer `claude` om je aan te melden met je LampTwist-account.' },
        { title: 'Installeer Git voor Windows', text: 'Claude Code heeft het nodig voor zijn tools. Laat de standaardopties staan.', button: { label: 'Git downloaden', href: 'https://git-scm.com/downloads/win' } },
        { title: 'Download Cody', text: 'Meld je aan op GitHub (toegang tot LampTwist/cody) en neem Cody-Setup-….exe.', button: { label: 'Laatste versie van Cody', href: RELEASES } },
        { title: 'Start het installatieprogramma', text: 'Windows kan "Windows heeft uw pc beschermd" tonen: normaal voor een interne app. Klik op "Meer informatie" en dan "Toch uitvoeren". Cody installeert en opent.' },
        { title: 'Eerste keer', text: 'Sta meldingen toe en druk op Ctrl+Shift+T om een project te kiezen. Het venster sluiten houdt Cody actief in de taakbalk (icoon bij de klok).' },
      ],
    },
    updatesTitle: 'Updates',
    updatesText: 'Automatisch. Bij een nieuwe versie krijg je een melding en een balk "Update beschikbaar": klik op Update en dan Restart. Je sessies komen terug na de herstart.',
    shortcutsTitle: 'Handige sneltoetsen',
    shortcutsMac: [['⌘K', 'Alle commando’s'], ['⌘T', 'Nieuwe sessie'], ['⌘E', 'Terminal of chat'], ['⌘J', 'Sessie die op je wacht'], ['⌘1…9', 'Van sessie wisselen']],
    shortcutsWin: [['Ctrl+Shift+K', 'Alle commando’s'], ['Ctrl+Shift+T', 'Nieuwe sessie'], ['Ctrl+Shift+E', 'Terminal of chat'], ['Ctrl+Shift+J', 'Sessie die op je wacht'], ['Ctrl+1…9', 'Van sessie wisselen']],
    helpTitle: 'Probleem?',
    help: [
      ['"claude" niet gevonden', 'Claude Code is niet geïnstalleerd of je bent niet aangemeld: doe stap 1 opnieuw.'],
      ['GitHub zegt "not found"', 'Je hebt nog geen toegang tot LampTwist/cody: vraag de uitnodiging.'],
      ['Op Windows vraagt Claude naar Git Bash', 'Installeer Git voor Windows (stap 2) en herstart Cody.'],
    ],
    contact: 'Nog altijd vast? Stuur een screenshot naar',
  },
  en: {
    videoCaption: 'Cody in one minute · sound on.',
    back: 'Back to apps',
    eyebrow: 'Internal tool · Mac & Windows',
    tagline: 'All your Claude Code sessions in one calm app. Cody tells you when Claude is done or needs you, and makes conversations easy to read.',
    features: [
      { title: 'It lets you know', text: 'A notification when Claude finishes or waits for an answer, even while you’re in another app. Slack or your phone when you’re away.' },
      { title: 'Everything in one place', text: 'Ten projects open? Each session shows its live status in the sidebar: working, needs you, done.' },
      { title: 'Terminal or chat', text: 'The real Claude Code, or a readable chat view with a message box, screenshots and ready-made prompts.' },
      { title: 'Your limits in view', text: 'Context, cost and your plan limits (5h, week, Fable) at a glance, with each project’s share.' },
    ],
    screenshotAlt: 'A Cody session',
    screenshotCaption: 'Claude Code on the left, the readable conversation on the right.',
    needTitle: 'What you need:',
    needText: 'a Claude account (LampTwist team) with Claude Code installed, and GitHub access to LampTwist/cody (ask Geoffrey). About 10 minutes, once.',
    installTitle: 'Install Cody',
    installSub: 'Pick your computer. We guessed it for you.',
    copy: 'Copy', copied: 'Copied',
    mac: {
      intro: 'On the Mac, Cody builds on your own machine (about a minute): no Apple developer account needed.',
      steps: [
        { title: 'Install Claude Code and log in', text: 'Open Terminal (⌘ Space, type "Terminal") and check:', code: 'claude --version', after: 'See a version number? Great. Otherwise install Claude Code from [claude.com/code](https://claude.com/code), then run `claude` once to log in.' },
        { title: 'Install Apple’s developer tools', code: 'xcode-select --install', after: 'A window pops up: click Install. If it says they’re already installed, that’s fine.' },
        { title: 'Connect GitHub', text: 'Accept the invite to LampTwist/cody on GitHub, then:', code: 'gh auth login', after: 'Choose GitHub.com, HTTPS and "Login with a web browser". No `gh`? `brew install gh`.' },
        { title: 'Install Cody', code: MAC_INSTALL, after: 'Cody builds and opens. From then on it’s in Applications. Keep the `cody` folder: updates come from there.' },
        { title: 'First launch', text: 'Allow notifications, then press ⌘T to pick a project. You’re set.' },
      ],
    },
    windows: {
      intro: 'On Windows you download an installer. Windows 10 or 11.',
      steps: [
        { title: 'Install Claude Code and log in', text: 'Open PowerShell and run:', code: 'irm https://claude.ai/install.ps1 | iex', after: 'Then type `claude` once to log in with your LampTwist account.' },
        { title: 'Install Git for Windows', text: 'Claude Code needs it for its tools. Keep the default options.', button: { label: 'Download Git', href: 'https://git-scm.com/downloads/win' } },
        { title: 'Download Cody', text: 'Sign in to GitHub (access to LampTwist/cody) and take Cody-Setup-….exe.', button: { label: 'Latest Cody release', href: RELEASES } },
        { title: 'Run the installer', text: 'Windows may say "Windows protected your PC": that’s normal for an internal app. Click "More info", then "Run anyway". Cody installs and opens.' },
        { title: 'First launch', text: 'Allow notifications, then Ctrl+Shift+T to pick a project. Closing the window keeps Cody running in the taskbar (icon near the clock).' },
      ],
    },
    updatesTitle: 'Updates',
    updatesText: 'Automatic. When there’s a new version you get a notification and an "Update available" banner: click Update, then Restart. Your sessions come back after the restart.',
    shortcutsTitle: 'Handy shortcuts',
    shortcutsMac: [['⌘K', 'Every command'], ['⌘T', 'New session'], ['⌘E', 'Terminal or chat'], ['⌘J', 'Session that needs you'], ['⌘1…9', 'Switch session']],
    shortcutsWin: [['Ctrl+Shift+K', 'Every command'], ['Ctrl+Shift+T', 'New session'], ['Ctrl+Shift+E', 'Terminal or chat'], ['Ctrl+Shift+J', 'Session that needs you'], ['Ctrl+1…9', 'Switch session']],
    helpTitle: 'Stuck?',
    help: [
      ['"claude" not found', 'Claude Code isn’t installed or logged in: redo step 1.'],
      ['GitHub says "not found"', 'You don’t have access to LampTwist/cody yet: ask for the invite.'],
      ['On Windows, Claude mentions Git Bash', 'Install Git for Windows (step 2), then restart Cody.'],
    ],
    contact: 'Still stuck? Send a screenshot to',
  },
};
